import { z } from 'zod';
import { deviseSchema, idSchema } from './commun.js';
export const TYPES_ESTIMATION = [
    'assurance',
    'marche',
    'remplacement',
    'succession',
    'donation',
];
export const TYPES_POLICE = ['clou_a_clou', 'stock', 'exposition'];
export const auteurEstimationSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('membre'), compteId: idSchema }),
    z.strictObject({ type: z.literal('expert'), nom: z.string().trim().min(1).max(200) }),
    z.strictObject({
        type: z.literal('maison_de_ventes'),
        nom: z.string().trim().min(1).max(200),
        contactId: idSchema.optional(),
    }),
]);
export const montantAssuranceSchema = z.int().min(0).max(100_000_000_000);
export const dateAssuranceSchema = z.iso
    .datetime({ offset: true })
    .transform((date) => new Date(date));
const champsEstimation = {
    exemplaireId: idSchema.optional(),
    type: z.enum(TYPES_ESTIMATION),
    montantCentimes: montantAssuranceSchema.optional(),
    devise: deviseSchema,
    dateEffet: dateAssuranceSchema,
    auteur: auteurEstimationSchema,
    methode: z.string().trim().max(2000).optional(),
    justification: z.string().trim().max(5000).optional(),
    mediaIds: z.array(idSchema).max(100).default([]),
    documentIds: z.array(idSchema).max(100).default([]),
    annuleId: idSchema.optional(),
    motif: z.string().trim().min(1).max(2000).optional(),
};
export const creationEstimationSchema = z
    .strictObject(champsEstimation)
    .superRefine((estimation, ctx) => {
    if (estimation.montantCentimes === undefined && !estimation.annuleId)
        ctx.addIssue({
            code: 'custom',
            path: ['montantCentimes'],
            message: 'Un montant est requis hors annulation.',
        });
    if (estimation.annuleId && !estimation.motif)
        ctx.addIssue({
            code: 'custom',
            path: ['motif'],
            message: 'Une annulation demande un motif.',
        });
});
export const estimationSchema = z
    .strictObject({
    ...champsEstimation,
    _id: idSchema,
    oeuvreId: idSchema,
    creePar: idSchema.nullable(),
    creeLe: z.date(),
    dateEffet: z.date(),
})
    .superRefine((estimation, ctx) => {
    if (estimation.montantCentimes === undefined && !estimation.annuleId)
        ctx.addIssue({
            code: 'custom',
            path: ['montantCentimes'],
            message: 'Un montant est requis hors annulation.',
        });
    if (estimation.annuleId && !estimation.motif)
        ctx.addIssue({
            code: 'custom',
            path: ['motif'],
            message: 'Une annulation demande un motif.',
        });
});
export const champsPolice = {
    assureurContactId: idSchema,
    numero: z.string().trim().min(1).max(120),
    type: z.enum(TYPES_POLICE),
    debut: dateAssuranceSchema,
    fin: dateAssuranceSchema,
    plafondGlobalCentimes: montantAssuranceSchema.optional(),
    plafondParOeuvreCentimes: montantAssuranceSchema.optional(),
    franchiseCentimes: montantAssuranceSchema.optional(),
    devise: deviseSchema,
    lieuIds: z.array(idSchema).max(500).default([]),
    oeuvreIds: z.array(idSchema).max(500).default([]),
    documentId: idSchema.optional(),
    mediaId: idSchema.optional(),
    archivee: z.boolean().default(false),
};
export const creationPoliceSchema = z
    .strictObject(champsPolice)
    .refine((police) => police.fin > police.debut, {
    path: ['fin'],
    message: 'La fin doit suivre le début.',
});
export const policeSchema = z
    .strictObject({
    ...champsPolice,
    _id: idSchema,
    debut: z.date(),
    fin: z.date(),
    creePar: idSchema.nullable(),
    modifieLe: z.date(),
    revision: z.int().min(0),
})
    .refine((police) => police.fin > police.debut, {
    path: ['fin'],
    message: 'La fin doit suivre le début.',
});
export const reglagesAssuranceSchema = z.strictObject({
    cle: z.literal('assurance').default('assurance'),
    periodiciteMois: z.int().min(1).max(120).default(24),
    seuilComparablePct: z.int().min(1).max(500).default(20),
    revision: z.int().min(0).default(0),
});
/** Le périmètre œuvre exclut les estimations propres à ses exemplaires. */
export function valeurAssureeCourante(estimations, maintenant, exemplaireId) {
    const annulees = new Set(estimations.filter((e) => e.annuleId && e.creeLe <= maintenant).map((e) => e.annuleId));
    return (estimations
        .filter((e) => e.type === 'assurance' &&
        e.montantCentimes !== undefined &&
        e.exemplaireId === exemplaireId &&
        !annulees.has(e._id) &&
        e.dateEffet <= maintenant)
        .sort((a, b) => b.dateEffet.getTime() - a.dateEffet.getTime() ||
        b.creeLe.getTime() - a.creeLe.getTime() ||
        b._id.localeCompare(a._id))[0] ?? null);
}
export function couverture(oeuvre, polices, maintenant) {
    return polices
        .filter((p) => !p.archivee && p.debut <= maintenant && maintenant < p.fin)
        .flatMap((police) => {
        if (police.oeuvreIds.includes(oeuvre._id))
            return [{ police, raison: 'rattachement' }];
        if (oeuvre.localisationId && police.lieuIds.includes(oeuvre.localisationId))
            return [{ police, raison: 'lieu' }];
        return [];
    });
}
export function estimationsParOeuvre(estimations) {
    const parOeuvre = new Map();
    for (const estimation of estimations) {
        const historique = parOeuvre.get(estimation.oeuvreId) ?? [];
        historique.push(estimation);
        parOeuvre.set(estimation.oeuvreId, historique);
    }
    return parOeuvre;
}
export function controlePlafonds(oeuvres, estimations, police, maintenant) {
    const parOeuvre = estimationsParOeuvre(estimations);
    const couvertes = oeuvres.filter((o) => couverture(o, [police], maintenant).length > 0);
    const valeurs = couvertes.map((oeuvre) => ({
        oeuvreId: oeuvre._id,
        valeur: valeurAssureeCourante(parOeuvre.get(oeuvre._id) ?? [], maintenant),
    }));
    const compatibles = valeurs.filter((ligne) => ligne.valeur?.devise === police.devise);
    const totalCentimes = compatibles.reduce((total, ligne) => total + (ligne.valeur?.montantCentimes ?? 0), 0);
    return {
        totalCentimes,
        depassementGlobal: police.plafondGlobalCentimes !== undefined && totalCentimes > police.plafondGlobalCentimes,
        depassementsOeuvres: compatibles
            .filter((ligne) => police.plafondParOeuvreCentimes !== undefined &&
            (ligne.valeur?.montantCentimes ?? 0) > police.plafondParOeuvreCentimes)
            .map((ligne) => ({
            oeuvreId: ligne.oeuvreId,
            montantCentimes: ligne.valeur.montantCentimes,
        })),
        devisesDifferentes: valeurs
            .filter((ligne) => ligne.valeur && ligne.valeur.devise !== police.devise)
            .map((ligne) => ({
            oeuvreId: ligne.oeuvreId,
            montantCentimes: ligne.valeur.montantCentimes,
            devise: ligne.valeur.devise,
            raison: 'devise_differente',
        })),
    };
}
export function oeuvresAReevaluer(oeuvres, estimations, ventes, reglages, maintenant) {
    const aReevaluer = [];
    const sansValeur = [];
    const parId = new Map(oeuvres.map((o) => [o._id, o]));
    const parOeuvre = estimationsParOeuvre(estimations);
    for (const oeuvre of oeuvres) {
        if (['vendue', 'archivee'].includes(oeuvre.statut))
            continue;
        const valeur = valeurAssureeCourante(parOeuvre.get(oeuvre._id) ?? [], maintenant);
        if (!valeur) {
            sansValeur.push(oeuvre);
            continue;
        }
        const raisons = [];
        const limite = new Date(valeur.dateEffet);
        const jour = limite.getUTCDate();
        limite.setUTCDate(1);
        limite.setUTCMonth(limite.getUTCMonth() + reglages.periodiciteMois);
        const dernierJour = new Date(Date.UTC(limite.getUTCFullYear(), limite.getUTCMonth() + 1, 0)).getUTCDate();
        limite.setUTCDate(Math.min(jour, dernierJour));
        if (limite <= maintenant)
            raisons.push({ type: 'periodicite' });
        for (const vente of ventes) {
            const autre = parId.get(vente.oeuvreId);
            if (!autre ||
                autre._id === oeuvre._id ||
                autre.artisteId !== oeuvre.artisteId ||
                autre.type !== oeuvre.type ||
                vente.date <= valeur.dateEffet ||
                vente.date > maintenant ||
                vente.devise !== valeur.devise ||
                vente.prixCentimes * 100 <= valeur.montantCentimes * (100 + reglages.seuilComparablePct))
                continue;
            const surface = (oeuvre.dimensions?.hauteur ?? 0) * (oeuvre.dimensions?.largeur ?? 0);
            const autreSurface = (autre.dimensions?.hauteur ?? 0) * (autre.dimensions?.largeur ?? 0);
            if (surface <= 0 ||
                autreSurface <= 0 ||
                autreSurface / surface < 0.8 ||
                autreSurface / surface > 1.25)
                continue;
            raisons.push({ type: 'vente_comparable', vente });
        }
        if (raisons.length)
            aReevaluer.push({ oeuvre, raisons });
    }
    return { aReevaluer, sansValeur };
}
export function echeancesPolices(polices, maintenant) {
    return polices
        .filter((p) => !p.archivee)
        .flatMap((police) => {
        const jours = (police.fin.getTime() - maintenant.getTime()) / 86_400_000;
        if (jours <= -30 || jours > 60)
            return [];
        let jalon = 'j60';
        if (jours <= 0)
            jalon = 'echue';
        else if (jours <= 30)
            jalon = 'j30';
        return [{ police, jalon }];
    });
}
//# sourceMappingURL=assurance.js.map